import { useEffect, useCallback, useMemo, useState, ReactNode } from "react";
import {
  Page,
  Card,
  Text,
  BlockStack,
  TextContainer,
  Button,
  ButtonGroup,
  InlineStack,
  EmptyState,
  Link,
  Spinner,
  Badge,
  Tooltip,
} from "@shopify/polaris";
import "./style.scss";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import PrettyDate from "components/format/PrettyDate";
import UploadFile from "features/upload-file/upload-file";
import Files from "features/files/files";
import { fetchFileDetails, setDestroy } from "containers/file/file.Slice";
import { RootState, AppDispatch } from "configureStore";
import { useSelector, useDispatch } from "react-redux";
import {
  setModalxComponent,
  setModalxComponentProps,
  setModalxStyle,
} from "components/modalx/modalx.slice";
import { History, Link2, Unlink2, Ban } from "lucide-react";
import { API_URL } from "../../constants";
import FileViewSkeleton from "containers/file/FileViewSkeleton";
import SignerCards, { SignerCard } from "containers/file/SignerCards";
import CommentsFeature from "components/comment/comments";
import { CommentTypes } from "types/Comment";
import BuildPdf from "components/customer/build-pdf/BuildPdf";
import { hasPdfExtension } from "utils/helperFunctions";
import { formatFileNumberWithSubNumber } from "utils/formatFileNumberDisplay";
import type { UserSigner } from "types/DokobitMeta";
import {
  getNonDisabledUserIds,
  isSignatureLockedFile,
  resolveKnownUserName,
  canWithdrawDocument as computeCanWithdrawDocument,
} from "utils/signatureUtils";
import {
  getContactSignersArray,
  renderContactSignerStatuses,
} from "utils/contactSignerTableUtils";
import {
  openDokobitSignUrl,
  createHandleRepeatEmail,
} from "utils/signatureViewUtils";
import { AuditLogPopover } from "components/audit-log/audit-log-popover";
import { isLeaveSubject } from "features/leave-calendar/resolveLeaveTemplateClient";
import AddToCartButton from "features/document-cart/AddToCartButton";
import AttachToDocumentModal from "components/attach-to-document/AttachToDocumentModal";
import MakeMainDocumentModal from "components/attach-to-document/MakeMainDocumentModal";
import DocumentAnnulmentPanel from "components/document-annulment/DocumentAnnulmentPanel";
import { fetchCategoryDetails } from "containers/document/category-tree/category-tree.Slice";

// Text-only actions in the document card; on a phone and on a touch screen they get a
// finger-sized height instead of the 20px of their text line.
const textActionClass =
  "inline-flex items-center gap-1.5 bg-transparent border-0 p-0 cursor-pointer text-sm hover:underline max-md:min-h-11 [@media(pointer:coarse)]:min-h-11";

// renderContactSignerStatuses (shared with the other document pages) answers
// table rows shaped [name, date, status]; the cards want those parts by name.
function contactSignerRowsToCards(rows: ReactNode[][]): SignerCard[] {
  return rows.map(([name, date, status]) => ({ name, date, status }));
}

function FileContainer({ source = "" }) {
  const { t } = useTranslation();
  const { companyId, catId, fileId } = useParams();
  const dispatch: AppDispatch = useDispatch();
  const [isAttachModalOpen, setIsAttachModalOpen] = useState(false);
  const [isMakeMainModalOpen, setIsMakeMainModalOpen] = useState(false);
  // Bumped after a successful annul so DocumentAnnulmentPanel (which fetches
  // its own history, independent of the fileContainerReducer) refetches too.
  const [annulmentRefreshKey, setAnnulmentRefreshKey] = useState(0);
  // Stable across re-renders — a fresh array on every render would reset
  // AttachToDocumentModal's in-progress search each time this re-renders.
  const attachFileIds = useMemo(() => (fileId ? [fileId] : []), [fileId]);

  const loadFileData = useCallback(() => {
    if (fileId && companyId) {
      dispatch(fetchFileDetails({ fileId, companyId }));
    }
  }, [fileId, companyId, dispatch]);

  // Shared by every action that can move a document in or out of this
  // parent's child list (attach/detach for this document itself, and the
  // per-child-row menu below): refetches this page and the category tree's
  // per-category counts, which the parent-move endpoints don't otherwise
  // keep in sync on the client.
  const refreshAfterParentChange = useCallback(() => {
    loadFileData();
    if (companyId) {
      dispatch(
        fetchCategoryDetails({
          companyId,
          source: source === "profile" ? "profile" : "document",
        })
      );
    }
  }, [loadFileData, dispatch, companyId, source]);

  const handleResetSignaturesClick = () => {
    dispatch(setModalxComponent("ResetFileSignaturesConfirm"));
    dispatch(
      setModalxComponentProps({
        fileId,
        isFullySigned: !!fileEntity?.isSigned,
        onDone: () => setAnnulmentRefreshKey((key) => key + 1),
      })
    );
  };

  const handleAnnulClick = () => {
    dispatch(setModalxComponent("AnnulDocumentConfirm"));
    dispatch(
      setModalxComponentProps({
        fileId,
        annulEffects: fileEntity?.annulEffects ?? null,
        onDone: () => setAnnulmentRefreshKey((key) => key + 1),
      })
    );
  };

  const handleWithdrawClick = () => {
    dispatch(setModalxComponent("WithdrawDocumentConfirm"));
    dispatch(
      setModalxComponentProps({
        fileId,
        onDone: () => {
          if (fileId && companyId) dispatch(fetchFileDetails({ fileId, companyId }));
        },
      })
    );
  };

  const { fileEntity, isLoading } = useSelector(
    (state: RootState) => state.fileContainerReducer
  );

  const { role } = useSelector((state: RootState) => state.authReducer);

  useEffect(() => {
    loadFileData();

    return () => {
      dispatch(setDestroy());
    };
  }, [fileId, companyId, loadFileData]);

  const handleOnFileChange = () => { };

  const handleEditClick = (fileId: string) => {
    dispatch(setModalxComponent("FileForm"));
    dispatch(
      setModalxComponentProps({
        fileId,
        companyId,
        isCustomerSelectorVisible: true,
        categoryType: fileEntity?.category?.type,
      })
    );
  };

  const handleEditFile = (id: string, userId: string) => {
    dispatch(setModalxComponent("UserDocumentForm"));
    dispatch(
      setModalxComponentProps({
        user: userId,
        id,
        subject: fileEntity?.templateId?.subject || "user",
        signType: "Click",
        managerSigner: fileEntity?.user?.companyuser?.manager?.id,
      })
    );
  };

  const handleAddAdditionalSigners = (id: string) => {
    dispatch(setModalxComponent("AddAdditionalSigners"));
    dispatch(
      setModalxComponentProps({
        fileId: id,
      })
    );
  };

  const handleDeclineSignRequest = async (id: string) => {
    dispatch(setModalxComponent("DeclineClickSignConfirm"));
    dispatch(
      setModalxComponentProps({
        fileId: id,
      })
    );
  };

  const handleDeclineDokobitSignRequest = async (id: string) => {
    dispatch(setModalxComponent("DeclineDokobitSignConfirm"));
    dispatch(
      setModalxComponentProps({
        fileId: id,
      })
    );
  };

  // check strictly route param and fetch fileId
  const fetchedFileId = fileEntity?.id ?? null;
  if (fetchedFileId !== fileId) {
    return <FileViewSkeleton />;
  }

  if (!fileEntity) {
    return <FileViewSkeleton />;
  }

  const customFieldsContent = fileEntity.customFields
    ?.filter(({ value }) => value)
    .map(({ label, value, fieldType }) => {
      // Dates are always strings, only file fields can be arrays
      if (fieldType === "date") {
        return (
          <Text as="p" variant="bodyMd" key={label}>
            <strong>{t(label)} : </strong>
            <PrettyDate dateString={value as string} />
          </Text>
        );
      } else {
        // For non-date fields, handle arrays (like file fields)
        const displayValue = Array.isArray(value)
          ? value.join(", ")
          : (value as string);
        return (
          <Text as="p" variant="bodyMd" key={label}>
            <strong>{t(label)} : </strong> {displayValue}
          </Text>
        );
      }
    });

  const categoriesNode = fileEntity?.categories
    ?.map((categoryEntity) => {
      return categoryEntity.title;
    })
    .join(" ,");

  const ownCustomers = fileEntity?.customer || [];
  // A child document usually has no customer of its own — files/fetch-by-id
  // then returns its parent document's customers as `inheritedCustomer`, and
  // signing the child offers those customers' contacts.
  const inheritedCustomers = ownCustomers.length
    ? []
    : fileEntity?.inheritedCustomer || [];
  const isCustomerInherited = inheritedCustomers.length > 0;
  const customers = isCustomerInherited ? inheritedCustomers : ownCustomers;
  const customerIds = customers.map((customerEntity) => customerEntity.id);
  const customersNode = customers
    ?.map((customerEntity) => {
      return customerEntity.name;
    })
    .join(" ,");

  const FileSignUserAssignHandler = (
    fileId: string,
    userSigners?: string[] | undefined | any,
    customerId?: string | null,
    contactSigners?: any[],
    signType?: any
  ) => {
    const customersList = customers;
    const customerIdentifiers = customerIds;
    const singleCustomerId =
      customersList.length === 1 ? customersList[0]?.id : null;
    const resolvedCustomerId =
      customerId !== undefined ? customerId : singleCustomerId;
    const usersList = getNonDisabledUserIds(userSigners);
    dispatch(setModalxComponent("FileSignMultiUserAssignPopup"));
    dispatch(
      setModalxComponentProps({
        fileId,
        companyId,
        usersList,
        customerId: resolvedCustomerId,
        customerIds: customerIdentifiers,
        customers: customersList,
        contactSigners: contactSigners || [],
        signType,
        isTemplateGenerated: isFileSignatureLocked,
      })
    );
  };

  const handlePdfView = (id: string) => {
    dispatch(setModalxComponent("PdfView"));
    dispatch(
      setModalxComponentProps({
        id,
        companyId,
      })
    );
  };

  // "Reminder history": opens the reminder modal, whose second section lists
  // every reminder ever sent for this document. Reminders only — the backend
  // writes a SignatureReminders row when a reminder goes out, never when the
  // document is first sent for signing, so the label must not promise the
  // request leg as well.
  //
  // Until now that modal was reachable only from the "unsigned assigned
  // documents" widget, which drops a document as soon as nothing is awaited —
  // so cancelling the signing, or finishing it, hid the send history for good
  // (Desk #136: the trail a lawyer needs is exactly the one of a document that
  // is no longer pending). Hence no gating here on signType, isSigned or the
  // presence of Signatures rows: the history outlives all three.
  //
  // Props mirror the widget's call site — the modal takes fileId + companyId
  // only and refreshes the unsigned list itself when settings are saved, so
  // there is no callback for this page to pass.
  const handleSigningHistoryClick = () => {
    dispatch(setModalxComponent("ReminderSettingsModal"));
    dispatch(setModalxStyle("large"));
    dispatch(
      setModalxComponentProps({
        fileId: fileEntity.id,
        companyId,
      })
    );
  };

  // check file is generated by user template
  // only user can edit file if not signed otherwise master can edit by general form
  // manager can do comment only
  // disable child files upload for user generated file
  const isUserTemplate = isLeaveSubject(fileEntity.templateId?.subject || "");
  const isAssetsTemplate = fileEntity.templateId?.subject === "assets";
  const isSigned = fileEntity.isSigned;
  const isAnnulled = !!fileEntity.annulledAt;
  const isUserUploader = fileEntity.user?.id === role?.user;
  const isUserMaster = role?.permissionsgroups?.includes("Master");
  // Owner's signing rule: a document generated from a template may never have
  // a signature/signature request removed one by one — only withdrawn whole.
  // Backend contract v2: some template-generated documents (e.g. work-security
  // roles) are deliberately NOT locked — isSignatureLockedFile reads the
  // authoritative signaturesLocked flag when present.
  const isFileSignatureLocked = isSignatureLockedFile(fileEntity);
  const isWithdrawn = !!fileEntity.withdrawnAt;
  const canWithdrawDocument = computeCanWithdrawDocument(fileEntity, {
    currentUserId: role?.user,
    isMaster: isUserMaster,
  });
  // «Pridėti parašų»: adding a signer to a signed document reopens it, which
  // the backend (signature/add-signers) allows to Master or Signature only.
  const canAddSigners =
    !isAnnulled &&
    !isWithdrawn &&
    (!isSigned ||
      ["Master", "Signature"].some((permission) =>
        role?.permissionsgroups?.includes(permission)
      ));
  const withdrawnByName = isWithdrawn
    ? resolveKnownUserName(fileEntity, fileEntity.withdrawnBy)
    : null;

  // The route (documents/:catId/:fileId) is already gated to these three,
  // but this container has other, differently-gated entry points elsewhere
  // in the app, so the parent-move actions re-check it defensively.
  const canManageDocumentParent = ["Master", "SalesMan", "Access document"].some(
    (permission) => role?.permissionsgroups?.includes(permission)
  );
  // fetch-by-id.js populates `relatedTo` into the full parent file, not just
  // its id (unlike the PATCH .../parent response) — accept either shape.
  const parentRef = fileEntity.relatedTo as unknown as
    | { id?: string }
    | string
    | null
    | undefined;
  const parentFileId =
    (typeof parentRef === "string" ? parentRef : parentRef?.id) || null;
  const hasParentDocument = Boolean(parentFileId);
  // `order` is attached by files/fetch-by-id but not declared on FileEntity.
  const isModuleManagedDocument = Boolean((fileEntity as any).order);
  // Order amendments carry no `order`/systemTemplateKey of their own (custom
  // template) — set-parent.js blocks them by subNumber instead, mirror that.
  const isOrderAmendmentDocument =
    (fileEntity as any).subNumber != null &&
    Number((fileEntity as any).subNumber) > 0;
  const canMoveDocumentParent =
    canManageDocumentParent &&
    !isUserTemplate &&
    !isModuleManagedDocument &&
    !isOrderAmendmentDocument;

  const customerId =
    customers.length && customers.length === 1 ? customers[0]?.id : null;
  const isPdf = hasPdfExtension(fileEntity?.title || "");

  const dokobitAllSigners = [
    ...(fileEntity?.dokobit?.signersList || []),
    ...getContactSignersArray(fileEntity?.dokobit?.contactSigners),
  ];
  const dokobitSignedCount = dokobitAllSigners.filter(
    (signer: any) => signer.signed
  ).length;
  const hasCollectedDokobitSignatures = dokobitSignedCount > 0;

  const handleCancelSignerRequest = (signer: any) => {
    if (!signer?.signatureId) return;
    dispatch(setModalxComponent("DokobitSignerRemoveConfirm"));
    dispatch(
      setModalxComponentProps({
        fileId: fileEntity.id,
        signatureId: signer.signatureId,
        signerName: signer.name,
        signedCount: dokobitSignedCount,
        totalCount: dokobitAllSigners.length,
      })
    );
  };

  const clickSignersList = fileEntity?.signersList || [];
  // Legacy migrated Dokobit rows carry dokobitmeta with status still
  // 'pending' — the backend treats them as collected signatures, mirror that.
  const isCollectedClickRow = (signer: any) =>
    signer.status === "signed" || !!signer.dokobitmeta;
  const clickSignedCount = clickSignersList.filter(isCollectedClickRow).length;
  const hasCollectedClickSignatures = clickSignedCount > 0;

  // Manager-approval rows are removable too: a stale one (manager changed,
  // manager declined) blocks completion forever, and removal cannot bypass
  // approval — the backend recomputes it from the template and the current
  // manager, not from row existence.
  const canCancelClickSigner = (signer: any) =>
    !isCollectedClickRow(signer) &&
    !fileEntity.isSigned &&
    !isFileSignatureLocked;

  // For Click rows the signature id is simply the Signatures row id
  const handleCancelClickSignerRequest = (signer: any) => {
    if (!signer?.id) return;
    dispatch(setModalxComponent("ClickSignerRemoveConfirm"));
    dispatch(
      setModalxComponentProps({
        fileId: fileEntity.id,
        signatureId: signer.id,
        signerName: signer?.user?.name || t("contact"),
        signedCount: clickSignedCount,
        totalCount: clickSignersList.length,
      })
    );
  };

  const handleReviewClick = ({
    id,
    fileId,
    disabled,
  }: {
    id: string;
    fileId: string;
    disabled: boolean;
  }) => {
    if (id && !disabled) {
      dispatch(setModalxComponent("FileReviewConfirmPopup"));
      dispatch(setModalxComponentProps({ id, fileId }));
    }
  };

  const handleClickSign = (fileId: string) => {
    dispatch(setModalxComponent("ClickToSignConfirm"));
    dispatch(setModalxComponentProps({ fileId }));
  };

  const handleDokobitSign = (fileId: string) =>
    openDokobitSignUrl(companyId!, fileId);

  const handleRepeatEmail = createHandleRepeatEmail(companyId!, () => {
    if (fileId) dispatch(fetchFileDetails({ fileId, companyId: companyId! }));
  });

  const handleDownloadFileVersion = (fileId: string, signatureId: string) => {
    // Open the file version download in a new tab
    window.open(
      `${API_URL}/${companyId}/files/download/${fileId}/signature/${signatureId}`,
      "_blank"
    );
  };

  return (
    <Page
      fullWidth
      title={fileEntity.name}
      titleMetadata={
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {isLoading && <Spinner size="small" />}
          {isAnnulled && <Badge tone="critical">{t("annulled_badge")}</Badge>}
          {isWithdrawn && <Badge tone="warning">{t("withdrawn_badge")}</Badge>}
          <AuditLogPopover
            companyId={companyId}
            subjectType="File"
            subjectId={String(fileId)}
            label={t("history", { defaultValue: "History" })}
          />
        </div>
      }
    >
      {/* Below lg the document's own column (data, signature) comes before the
          comments and related files, so nobody scrolls past them to sign. */}
      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1fr)_22rem] xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        {companyId && fileId && (
          <div className="min-w-0 lg:col-span-2">
            <DocumentAnnulmentPanel
              companyId={companyId}
              fileId={fileId}
              fileName={fileEntity.name || fileEntity.title}
              refreshKey={annulmentRefreshKey}
              annulledAt={fileEntity.annulledAt}
            />
          </div>
        )}
        <div className="min-w-0 max-lg:order-1">
          <BlockStack gap="500">
            <Card roundedAbove="sm">
              <CommentsFeature id={fileId} CommentTypes={CommentTypes.Files} />
            </Card>
            {!isUserTemplate && (
              <Card roundedAbove="sm">
                <div className="flex">
                  <Text as="h2" variant="headingSm">
                    {t("files")}
                  </Text>
                  <div className="flex-grow"></div>
                  <ButtonGroup>
                    <UploadFile
                      onChange={handleOnFileChange}
                      parentFileId={fileId}
                      categoryId={undefined}
                      categoryType={fileEntity.category?.type}
                    />
                    <BuildPdf
                      customerId={fileEntity?.customer[0]?.id}
                      isDisabled={false}
                      templateId={fileEntity.templateId?.id}
                      fileId={fileEntity.id}
                      restrictions={["user"]}
                    />
                  </ButtonGroup>
                </div>
                {fileEntity.relatedFiles.length > 0 ? (
                  <Files
                    files={fileEntity.relatedFiles}
                    source={"document"}
                    parentFileCustomerId={customerId || ""}
                    parentFileCustomers={(Array.isArray(customers) ? customers : []) as any}
                    allowParentActions={canManageDocumentParent}
                    onDocumentMoved={refreshAfterParentChange}
                    parentCategoryId={catId}
                    onChanged={loadFileData}
                  />
                ) : (
                  <EmptyState
                    heading={t("no_files")}
                    image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                  >
                    <UploadFile
                      onChange={handleOnFileChange}
                      parentFileId={fileId}
                      categoryId={undefined}
                      categoryType={fileEntity.category?.type}
                    />
                  </EmptyState>
                )}
              </Card>
            )}
          </BlockStack>
        </div>
        <div className="flex min-w-0 flex-col gap-5">
          <BlockStack gap="500">
            <Card roundedAbove="sm">
              <BlockStack gap="400">
                <AddToCartButton
                  fileId={fileEntity.id}
                  variant="prominent"
                  source={source === "profile" ? "profile" : "document"}
                />
                <InlineStack align="space-between">
                  <Link
                    url={`${API_URL}/${companyId}/files/download/${fileEntity.id}`}
                    target="_blank"
                    removeUnderline
                  >
                    {t("download")}
                  </Link>
                  {/* file uploaded user, not signed and (template type === 'user') then form should be open tempalte file edit*/}
                  {isUserTemplate &&
                    isUserUploader &&
                    !isSigned &&
                    !isAnnulled &&
                    !isWithdrawn && (
                    <Button
                      variant="plain"
                      onClick={() => {
                        handleEditFile(fileEntity.id, fileEntity.user?.id);
                      }}
                      disabled={isSigned}
                    >
                      {t("edit")}
                    </Button>
                  )}
                  {/* general file form will open in case of user having master permission */}
                  {isUserMaster &&
                    ((isUserTemplate && isSigned) || !isUserTemplate) && (
                      <Button
                        variant="plain"
                        onClick={() => handleEditClick(fileEntity.id)}
                        accessibilityLabel="Preview"
                      >
                        {t("edit")}
                      </Button>
                    )}
                </InlineStack>
                {/* Deliberately ungated: a cancelled or completed document must
                    still expose its reminder history. */}
                <InlineStack align="start">
                  <button
                    type="button"
                    onClick={handleSigningHistoryClick}
                    className={`${textActionClass} text-sky-700`}
                  >
                    <History size={16} aria-hidden="true" />
                    <span>{t("signing_history")}</span>
                  </button>
                </InlineStack>
                {canWithdrawDocument && (
                  <InlineStack align="start">
                    <button
                      type="button"
                      onClick={handleWithdrawClick}
                      className={`${textActionClass} text-red-700`}
                    >
                      <Ban size={16} aria-hidden="true" />
                      <span>{t("withdraw_document")}</span>
                    </button>
                  </InlineStack>
                )}
                {canMoveDocumentParent && (
                  <InlineStack align="start">
                    {hasParentDocument ? (
                      <button
                        type="button"
                        onClick={() => setIsMakeMainModalOpen(true)}
                        className={`${textActionClass} text-sky-700`}
                      >
                        <Unlink2 size={16} aria-hidden="true" />
                        <span>{t("make_main_document")}</span>
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setIsAttachModalOpen(true)}
                        className={`${textActionClass} text-sky-700`}
                      >
                        <Link2 size={16} aria-hidden="true" />
                        <span>{t("attach_to_document")}</span>
                      </button>
                    )}
                  </InlineStack>
                )}
              </BlockStack>
              <TextContainer>
                <Text as="p" variant="bodyMd">
                  <strong>{t("document_name")} : </strong>{" "}
                  {fileEntity?.name || fileEntity.title}
                </Text>
                <Text as="p" variant="bodyMd">
                  <strong>{t("customer")} : </strong> {customersNode}
                  {isCustomerInherited && (
                    <span className="text-zinc-500">
                      {" "}
                      {t("customer_inherited_from_parent")}
                    </span>
                  )}
                </Text>
                <Text as="p" variant="bodyMd">
                  <strong>{t("category")} : </strong> {categoriesNode}
                </Text>
                {fileEntity.assignedUser?.name && (
                  <Text as="p" variant="bodyMd">
                    <strong>{t("responsible_user")} : </strong>{" "}
                    {fileEntity.assignedUser?.name}
                  </Text>
                )}
                {fileEntity.user?.name && (
                  <Text as="p" variant="bodyMd">
                    <strong>{t("the_file_was_uploaded_by")} : </strong>{" "}
                    {fileEntity.user?.name}
                  </Text>
                )}
                {isWithdrawn && (
                  <Text as="p" variant="bodyMd">
                    <strong>{t("withdrawn_badge")} : </strong>{" "}
                    <PrettyDate
                      dateString={new Date(fileEntity.withdrawnAt as number).toISOString()}
                      showTime={true}
                    />
                    {withdrawnByName && ` — ${withdrawnByName}`}
                    {fileEntity.withdrawReason && (
                      <span className="text-zinc-500">
                        {" "}
                        ({fileEntity.withdrawReason})
                      </span>
                    )}
                  </Text>
                )}
                {!fileEntity.externalNumber ? (
                  <></>
                ) : (
                  <Text as="p" variant="bodyMd">
                    <strong>{t("externalNumber")} : </strong>{" "}
                    {fileEntity.externalNumber}
                  </Text>
                )}
                {fileEntity.series && (
                  <Text as="p" variant="bodyMd">
                    <strong>{t("series")} : </strong> {fileEntity.series.title}
                    {formatFileNumberWithSubNumber(
                      fileEntity.number,
                      fileEntity.subNumber
                    )}
                  </Text>
                )}
                {customFieldsContent}
                <Text as="p" variant="bodyMd">
                  <strong>{t("document_date")} : </strong>{" "}
                  {fileEntity.date ? (
                    <PrettyDate dateString={fileEntity.date} />
                  ) : (
                    "N/A"
                  )}
                </Text>
                <Text as="p" variant="bodyMd">
                  <strong>{t("dateTo")} : </strong>{" "}
                  {fileEntity.dateTo ? (
                    <PrettyDate dateString={fileEntity.dateTo} />
                  ) : (
                    "N/A"
                  )}
                </Text>
                <Text as="p" variant="bodyMd">
                  <strong>{t("createdAt")} : </strong>{" "}
                  <PrettyDate
                    dateString={fileEntity.createdAt}
                    showTime={true}
                  />
                </Text>
                <Text as="p" variant="bodyMd">
                  <strong>{t("updatedAt")} : </strong>{" "}
                  <PrettyDate
                    dateString={fileEntity.updatedAt}
                    showTime={true}
                  />
                </Text>
              </TextContainer>
            </Card>

            <Card roundedAbove="sm">
              <InlineStack align="center">{t("review_by")}</InlineStack>
              {fileEntity.reviewByUsers?.length > 0 ? (
                <div className="content_break content_margin">
                  {fileEntity.reviewByUsers.map((entity, key) => (
                    <Text variant="bodyMd" as="p" key={key}>
                      {t("review_by")} : {entity?.user?.name}{" "}
                      {entity.reviewed ? (
                        <>
                          <PrettyDate
                            dateString={entity.updatedAt}
                            showTime={true}
                          />{" "}
                          <Badge tone="success">{t("reviewed")}</Badge>{" "}
                        </>
                      ) : (
                        <>
                          <PrettyDate
                            dateString={entity?.createdAt || ""}
                            showTime={true}
                          />{" "}
                          <Badge tone="attention">{t("pending")}</Badge>{" "}
                          {role?.user === entity?.user?.id && (
                            <Button
                              onClick={() =>
                                handleReviewClick({
                                  id: entity.id,
                                  fileId: entity.file,
                                  disabled: entity.reviewed,
                                })
                              }
                              accessibilityLabel="Preview"
                            >
                              {t("mark_as_reviewed")}
                            </Button>
                          )}{" "}
                        </>
                      )}
                    </Text>
                  ))}
                </div>
              ) : (
                <>{t("no_review_required")}</>
              )}
            </Card>
            {isUserMaster &&
              isPdf &&
              !isAnnulled &&
              (fileEntity.isSigned ||
                fileEntity?.dokobit?.signersList?.length ||
                fileEntity?.signontab?.signersList?.length ||
                fileEntity?.signersList?.length) ? (
              <Card roundedAbove="sm">
                <BlockStack gap="200">
                  <Text as="h3" variant="headingSm">
                    {t("admin_actions")}
                  </Text>
                  <Text as="p" variant="bodyMd">
                    {t("remove_signed_document_description")}
                  </Text>
                  <Button
                    tone="critical"
                    variant="primary"
                    onClick={handleResetSignaturesClick}
                  >
                    {t("remove_signed_document")}
                  </Button>
                  {isSigned && (
                    <Button
                      tone="critical"
                      variant="primary"
                      onClick={handleAnnulClick}
                    >
                      {t("annul_document")}
                    </Button>
                  )}
                </BlockStack>
              </Card>
            ) : <></>}
          </BlockStack>
          {/* Below lg the signature card (status, sign, decline) is first, above the
              document's data, so nobody scrolls past them to sign. The wrapper
              is hidden while empty, or its flex gap would leave a blank strip. */}
          <div
            data-testid="signing-block"
            className="empty:hidden max-lg:order-first"
          >
            {/* if file source is "document"(it means files uploaded by document section) then dokobit signature details should be show */}
            {fileEntity.fileId && isPdf && fileEntity.signType != "Click" && (
              <Card roundedAbove="sm">
                <InlineStack align="center">
                  {t("document_signature")}
                </InlineStack>
                <InlineStack align="space-between">
                  <Button
                    variant="plain"
                    onClick={() => handlePdfView(fileEntity.id)}
                    accessibilityLabel="Preview"
                  >
                    {t("view")}
                  </Button>
                  {dokobitAllSigners.length > 0 &&
                    fileEntity.signType === "Dokobit" &&
                    canAddSigners && (
                      <Button
                        variant="plain"
                        onClick={() =>
                          handleAddAdditionalSigners(fileEntity.id)
                        }
                        accessibilityLabel="Preview"
                      >
                        {t("add_more_signers")}
                      </Button>
                    )}
                </InlineStack>

                <BlockStack gap="400">
                  {/* Dokobit Signers Table */}
                  {dokobitAllSigners.length > 0 &&
                    fileEntity.signType === "Dokobit" && (
                      <SignerCards
                        signers={[
                          ...(fileEntity.dokobit?.signersList || []).map(
                            (signer: any) => ({
                              name: signer.name,
                              date: signer.signed ? (
                                <PrettyDate
                                  dateString={signer.signing_time}
                                  showTime={true}
                                />
                              ) : (
                                <PrettyDate
                                  dateString={
                                    fileEntity?.dokobit?.createdAt || ""
                                  }
                                  showTime={true}
                                />
                              ),
                              status: signer.signed ? (
                                <Badge tone="success">{t("signed")}</Badge>
                              ) : signer.status === "rejected" ? (
                                <Badge tone="critical">{t("declined")}</Badge>
                              ) : signer.status === "withdrawn" ? (
                                <Badge>{t("signer_status_withdrawn")}</Badge>
                              ) : (
                                <Badge tone="attention">{t("pending")}</Badge>
                              ),
                              declineReason: signer.declineReason,
                              actions:
                                !signer.signed &&
                                !fileEntity.isSigned &&
                                !isWithdrawn ? (
                                  <>
                                    {signer.status !== "rejected" &&
                                      signer.id === role?.user && (
                                        <>
                                          <Button
                                            onClick={() =>
                                              handleDokobitSign(fileEntity.id)
                                            }
                                            accessibilityLabel="Sign"
                                          >
                                            {t("sign")}
                                          </Button>
                                          <Button
                                            onClick={() =>
                                              handleDeclineDokobitSignRequest(
                                                fileEntity.id
                                              )
                                            }
                                            accessibilityLabel="Decline"
                                          >
                                            {t("decline")}
                                          </Button>
                                        </>
                                      )}
                                    {signer.signatureId &&
                                      !isFileSignatureLocked && (
                                        <Button
                                          tone="critical"
                                          onClick={() =>
                                            handleCancelSignerRequest(signer)
                                          }
                                          accessibilityLabel="Cancel signature request"
                                        >
                                          {t("cancel_signer_request")}
                                        </Button>
                                      )}
                                  </>
                                ) : undefined,
                            })
                          ),
                          ...getContactSignersArray(
                            fileEntity?.dokobit?.contactSigners
                          ).map((signer: UserSigner) => ({
                            name: signer.name,
                            date: signer.signed ? (
                              <PrettyDate
                                dateString={signer.signing_time}
                                showTime={true}
                              />
                            ) : (
                              <PrettyDate
                                dateString={
                                  fileEntity?.dokobit?.createdAt || ""
                                }
                                showTime={true}
                              />
                            ),
                            status:
                              signer.status === "withdrawn" ? (
                                <Badge>{t("signer_status_withdrawn")}</Badge>
                              ) : signer.signed ? (
                                <Badge tone="success">{t("signed")}</Badge>
                              ) : (
                                <Badge tone="attention">{t("pending")}</Badge>
                              ),
                            actions:
                              !signer.signed &&
                              signer.signatureId &&
                              !fileEntity.isSigned &&
                              !isWithdrawn ? (
                                <>
                                  {signer.status !== "rejected" && (
                                    <Button
                                      onClick={() =>
                                        signer.signatureId &&
                                        handleRepeatEmail(signer.signatureId)
                                      }
                                      accessibilityLabel="Repeat email"
                                    >
                                      {t("repeat_email")}
                                    </Button>
                                  )}
                                  {!isFileSignatureLocked && (
                                    <Button
                                      tone="critical"
                                      onClick={() =>
                                        handleCancelSignerRequest(signer)
                                      }
                                      accessibilityLabel="Cancel signature request"
                                    >
                                      {t("cancel_signer_request")}
                                    </Button>
                                  )}
                                </>
                              ) : undefined,
                          })),
                        ]}
                      />
                    )}

                  {/* Signontab Signers Table */}
                  {fileEntity?.signontab?.signersList?.length &&
                    fileEntity.signType === "Signontab" && (
                      <SignerCards
                        signers={[
                          ...fileEntity.signontab.signersList.map(
                            (signer: any) => ({
                              name: signer.name,
                              date: signer.signed ? (
                                <PrettyDate
                                  dateString={signer.signing_time}
                                  showTime={true}
                                />
                              ) : (
                                <PrettyDate
                                  dateString={
                                    fileEntity?.signontab?.createdAt || ""
                                  }
                                  showTime={true}
                                />
                              ),
                              status:
                                signer.status === "withdrawn" ? (
                                  <Badge>{t("signer_status_withdrawn")}</Badge>
                                ) : signer.signed ? (
                                  <Badge tone="success">{t("signed")}</Badge>
                                ) : (
                                  <Badge tone="attention">{t("pending")}</Badge>
                                ),
                            })
                          ),
                          ...contactSignerRowsToCards(
                            renderContactSignerStatuses(
                              getContactSignersArray(
                                fileEntity?.signontab?.contactSigners
                              ),
                              fileEntity?.signontab?.createdAt || "",
                              t,
                              { columns: 3 }
                            )
                          ),
                        ]}
                      />
                    )}

                  {!fileEntity?.dokobit?.signersPayload &&
                    !isSigned &&
                    !isWithdrawn &&
                    fileEntity.signType != "Click" &&
                    !fileEntity?.signontab && (
                      <InlineStack align="center">
                        <Button
                          onClick={() =>
                            FileSignUserAssignHandler(
                              fileEntity.id,
                              [],
                              customerId
                            )
                          }
                          accessibilityLabel="Send signature request"
                        >
                          {t("document_send_signature_request")}
                        </Button>
                      </InlineStack>
                    )}

                  {fileEntity?.dokobit?.signersPayload &&
                    !fileEntity?.isSigned &&
                    !isWithdrawn && (
                      <InlineStack align="center">
                        {hasCollectedDokobitSignatures ? (
                          // Editing re-creates the whole Dokobit signing and
                          // would discard the signatures collected so far —
                          // once someone has signed, signers can only be
                          // removed/added one by one.
                          <Tooltip
                            content={t("edit_sign_request_disabled_partial")}
                          >
                            <Button
                              disabled
                              accessibilityLabel="Edit signature request"
                            >
                              {t("edit_sign_request")}
                            </Button>
                          </Tooltip>
                        ) : (
                          <Button
                            onClick={() =>
                              FileSignUserAssignHandler(
                                fileEntity.id,
                                fileEntity?.dokobit?.signersList,
                                customerId,
                                fileEntity?.dokobit?.contactSigners || [],
                                fileEntity?.signType
                              )
                            }
                            accessibilityLabel="Edit signature request"
                          >
                            {t("edit_sign_request")}
                          </Button>
                        )}
                      </InlineStack>
                    )}

                  {isSigned && (
                    <InlineStack align="center">
                      <Badge tone="success">{t("signed")}</Badge>
                    </InlineStack>
                  )}
                </BlockStack>
              </Card>
            )}

            {/* if document generated by the user and then "file uploaded" user and manager can click to sign the file */}
            {fileEntity.signType === "Click" && (
              <Card roundedAbove="sm">
                <InlineStack align="center">
                  {t("document_signature")}
                </InlineStack>
                <InlineStack align="space-between">
                  <Button
                    variant="plain"
                    onClick={() => handlePdfView(fileEntity.id)}
                    accessibilityLabel="Preview"
                  >
                    {t("view")}
                  </Button>
                  {canAddSigners && (
                    <Button
                      variant="plain"
                      onClick={() => handleAddAdditionalSigners(fileEntity.id)}
                      accessibilityLabel="Add more signers"
                    >
                      {t("add_more_signers")}
                    </Button>
                  )}
                  {!fileEntity.isSigned &&
                    !isWithdrawn &&
                    !isFileSignatureLocked &&
                    (hasCollectedClickSignatures ? (
                      // Editing re-posts the whole signer list; once someone
                      // has signed, signers can only be removed/added one by
                      // one so collected signatures are never re-requested.
                      <Tooltip
                        content={t("edit_sign_request_disabled_partial")}
                      >
                        <Button variant="plain" disabled accessibilityLabel="Edit">
                          {t("edit")}
                        </Button>
                      </Tooltip>
                    ) : (
                      <Button
                        variant="plain"
                        onClick={() =>
                          FileSignUserAssignHandler(
                            fileEntity.id,
                            fileEntity?.signersList,
                            customerId,
                            [],
                            fileEntity?.signType
                          )
                        }
                        accessibilityLabel="Edit"
                      >
                        {t("edit")}
                      </Button>
                    ))}
                </InlineStack>
                <BlockStack gap="400">
                  {fileEntity?.signersList?.length > 0 && (
                    <SignerCards
                      signers={fileEntity.signersList.map((signer: any) => ({
                        name: signer?.user?.name || "",
                        date:
                          signer.status === "signed" ? (
                            <PrettyDate
                              dateString={signer.updatedAt}
                              showTime={true}
                            />
                          ) : (
                            <PrettyDate
                              dateString={signer.createdAt}
                              showTime={true}
                            />
                          ),
                        status:
                          signer.status === "signed" ? (
                            <Badge tone="success">{t("signed")}</Badge>
                          ) : signer.status === "rejected" ? (
                            <Badge tone="critical">{t("declined")}</Badge>
                          ) : signer.status === "withdrawn" ? (
                            <Badge>{t("signer_status_withdrawn")}</Badge>
                          ) : (
                            <Badge tone="attention">{t("pending")}</Badge>
                          ),
                        declineReason: signer?.declineReason,
                        file: signer.fileVersionId ? (
                          <Button
                            variant="plain"
                            onClick={() =>
                              handleDownloadFileVersion(
                                fileEntity.id,
                                signer.id
                              )
                            }
                            accessibilityLabel="Download version"
                          >
                            {t("download")}
                          </Button>
                        ) : undefined,
                        actions:
                          !isWithdrawn &&
                          ((signer.status === "pending" &&
                            signer?.user?.id === role?.user) ||
                            canCancelClickSigner(signer)) ? (
                            <>
                              {signer.status === "pending" &&
                                signer?.user?.id === role?.user && (
                                  <>
                                    <Button
                                      onClick={() =>
                                        handleClickSign(fileEntity.id)
                                      }
                                      accessibilityLabel="Sign"
                                    >
                                      {t("sign")}
                                    </Button>
                                    <Button
                                      onClick={() =>
                                        handleDeclineSignRequest(fileEntity.id)
                                      }
                                      accessibilityLabel="Decline"
                                    >
                                      {t("decline")}
                                    </Button>
                                  </>
                                )}
                              {canCancelClickSigner(signer) && (
                                <Button
                                  tone="critical"
                                  onClick={() =>
                                    handleCancelClickSignerRequest(signer)
                                  }
                                  accessibilityLabel="Cancel signature request"
                                >
                                  {t("cancel_signer_request")}
                                </Button>
                              )}
                            </>
                          ) : undefined,
                      }))}
                    />
                  )}

                  {isSigned && (
                    <InlineStack align="center">
                      <Badge tone="success">{t("signed")}</Badge>
                    </InlineStack>
                  )}
                </BlockStack>
              </Card>
            )}
          </div>
        </div>
      </div>
      {companyId && fileId && (
        <>
          <AttachToDocumentModal
            companyId={companyId}
            fileIds={attachFileIds}
            open={isAttachModalOpen}
            onClose={() => setIsAttachModalOpen(false)}
            onDone={refreshAfterParentChange}
          />
          <MakeMainDocumentModal
            companyId={companyId}
            fileId={fileId}
            open={isMakeMainModalOpen}
            onClose={() => setIsMakeMainModalOpen(false)}
            onDone={refreshAfterParentChange}
          />
        </>
      )}
    </Page>
  );
}

export default FileContainer;
